import { useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import { Sparkles, RefreshCw, Gift, Lightbulb } from "lucide-react";

/* ============================================================
   CONFIG — hier kannst du alles später anpassen
   ============================================================ */

// Seite offline schalten: true = nur Hinweistext, false = normale Seite
const SITE_OFFLINE = true;
const OFFLINE_TITLE = "Kurze Pause.";
const OFFLINE_TEXT = "Das Ganze ist auf unbestimmte Zeit verschoben.";
const OFFLINE_SUB = "Vielleicht später. Vielleicht anders. Wer weiß.";

// Ziel des Countdowns: 1. August 2026, 12:00 Uhr, Europe/Berlin (CEST = UTC+2)
const COUNTDOWN_TARGET = new Date("2026-08-01T12:00:00+02:00");

// Bilder für das Memory-Spiel — hier später deine Bild-URLs eintragen.
// Beispiel: "/images/bild1.jpg" oder eine externe URL
const uploadedImages = [
  "https://customer-assets.emergentagent.com/job_natalia-good-vibes/artifacts/g0v2i921_734755c0-af0c-44be-9926-cdaba1b43318.jpeg",
  "https://customer-assets.emergentagent.com/job_natalia-good-vibes/artifacts/vyskacig_comic_nat.png",
  "https://customer-assets.emergentagent.com/job_natalia-good-vibes/artifacts/f5ebwlmx_crazy_natascha.png",
  "https://customer-assets.emergentagent.com/job_natalia-good-vibes/artifacts/c5k52hzy_crazy_natascha_seite.png",
];

// Passendes visuelles Filter-Variantenset pro Bildpaar
const VARIANTS = ["v-popart", "v-neon", "v-comic", "v-gradient", "v-duotone", "v-polaroid"];

// Hinweise, die nach jedem gefundenen Paar freigeschaltet werden
const hints = [
  "Hinweis 1: Es wird kein normaler Abend. Eher einer mit leichtem Pulsanstieg.",
  "Hinweis 2: Zieh etwas an, worin du dich wohlfühlst – aber bitte nicht zu overdressed. Du musst niemandem einen Heiratsantrag machen.",
  "Hinweis 3: Es wird ein bisschen Adrenalin, ein bisschen Wow und hoffentlich kein 'Ich bring dich um'.",
  "Hinweis 4: Diesmal darfst du mich in Stuttgart abholen.",
];

// Text der Gewinn-Nachricht + Platzhalter-Hinweis
const WIN_MSG_TITLE = "Okay… du hast gewonnen.";
const WIN_MSG_BODY =
  "Respekt. Du bist offiziell zu neugierig, um noch länger komplett im Dunkeln zu bleiben.";
const WIN_MSG_HINT = "Finaler Hinweis: Es wird hoch hinausgehen. Mehr sag ich nicht.";

// Reveal-Text nach dem Countdown-Ende — hier deine finale Überraschung eintragen
const REVEAL_TITLE = "Jetzt ist es soweit.";
const REVEAL_SUB = "Du hast lange genug gewartet … heute beginnt unser kleines Abenteuer.";
const REVEAL_PLACEHOLDER =
  "Hier kommt später die finale Date-Überraschung rein.";

// Micro-Texte (Pill Zeilen)
const MICRO_TEXTS = [
  "Geduld ist attraktiv. Angeblich.",
  "Noch ein bisschen warten … ich weiß, gemein.",
  "Keine Sorge, es lohnt sich.",
  "Bis dahin: Spiel eine Runde. Oder zwei. Oder fünf.",
];

/* ============================================================
   Utilities
   ============================================================ */
const PLACEHOLDER_COLORS = [
  ["#ff3d8b", "#a855f7"],
  ["#ff9645", "#fde047"],
  ["#22d3ee", "#a855f7"],
  ["#fde047", "#ff3d8b"],
  ["#a855f7", "#22d3ee"],
  ["#ff9645", "#ff3d8b"],
];
function placeholderDataUrl(i) {
  const [c1, c2] = PLACEHOLDER_COLORS[i % PLACEHOLDER_COLORS.length];
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'>` +
    `<defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>` +
    `<stop offset='0%' stop-color='${c1}'/><stop offset='100%' stop-color='${c2}'/>` +
    `</linearGradient></defs>` +
    `<rect width='200' height='200' fill='url(%23g)'/>` +
    `<text x='50%' y='58%' text-anchor='middle' font-family='sans-serif' ` +
    `font-size='72' font-weight='900' fill='white' opacity='0.85'>${i + 1}</text>` +
    `</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)
    .replace(/'/g, "%27")}`;
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/* ============================================================
   Countdown
   ============================================================ */
function useCountdown(target) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = target.getTime() - now;
  const done = diff <= 0;
  const d = Math.max(0, Math.floor(diff / 86400000));
  const h = Math.max(0, Math.floor((diff / 3600000) % 24));
  const m = Math.max(0, Math.floor((diff / 60000) % 60));
  const s = Math.max(0, Math.floor((diff / 1000) % 60));
  return { d, h, m, s, done };
}

function CountdownUnit({ value, label }) {
  const prev = useRef(value);
  const [tick, setTick] = useState(false);
  useEffect(() => {
    if (prev.current !== value) {
      setTick(true);
      const t = setTimeout(() => setTick(false), 320);
      prev.current = value;
      return () => clearTimeout(t);
    }
  }, [value]);
  return (
    <div className="cd-unit">
      <div className={`cd-num ${tick ? "tick" : ""}`}>
        {String(value).padStart(2, "0")}
      </div>
      <div className="cd-lbl">{label}</div>
    </div>
  );
}

function Countdown({ target }) {
  const { d, h, m, s, done } = useCountdown(target);
  if (done) return null;
  return (
    <section className="countdown-wrap" data-testid="countdown">
      <div className="countdown-label">Noch</div>
      <div className="countdown">
        <CountdownUnit value={d} label="Tage" />
        <CountdownUnit value={h} label="Std." />
        <CountdownUnit value={m} label="Min." />
        <CountdownUnit value={s} label="Sek." />
      </div>
      <div className="countdown-footer">
        Wenn die Zeit abgelaufen ist, erfährst du, wohin es geht.
      </div>
    </section>
  );
}

/* ============================================================
   Confetti
   ============================================================ */
function Confetti({ active }) {
  const [pieces, setPieces] = useState([]);
  const idRef = useRef(0);
  useEffect(() => {
    if (!active) return;
    const colors = ["#ff3d8b", "#a855f7", "#22d3ee", "#fde047", "#ff9645", "#ffffff"];
    const burst = [];
    for (let i = 0; i < 80; i++) {
      idRef.current += 1;
      burst.push({
        id: idRef.current,
        left: Math.random() * 100,
        color: colors[Math.floor(Math.random() * colors.length)],
        dx: (Math.random() - 0.5) * 280,
        spin: (Math.random() - 0.5) * 720,
        delay: Math.random() * 400,
        w: 6 + Math.random() * 6,
        h: 10 + Math.random() * 10,
      });
    }
    
    setPieces(burst);
    const t = setTimeout(() => setPieces([]), 3800);
    return () => clearTimeout(t);
  }, [active]);
  return (
    <div className="confetti-layer" aria-hidden="true">
      {pieces.map((p) => (
        <span
          key={p.id}
          className="confetti-piece"
          style={{
            left: `${p.left}%`,
            width: p.w,
            height: p.h,
            background: p.color,
            animationDelay: `${p.delay}ms`,
            "--dx": `${p.dx}px`,
            "--spin": `${p.spin}deg`,
          }}
        />
      ))}
    </div>
  );
}

/* ============================================================
   Memory Card
   ============================================================ */
function Card({ card, flipped, matched, onClick }) {
  const src = card.src || placeholderDataUrl(card.imgIdx);
  return (
    <div
      className={`card ${card.variant} ${flipped ? "flipped" : ""} ${matched ? "matched" : ""}`}
      onClick={onClick}
      data-testid="memory-card"
    >
      <div className="card-inner">
        <div className="card-face card-back">
          <div className="qmark">?</div>
        </div>
        <div className="card-face card-front">
          <img
            className="card-image"
            src={src}
            alt=""
            onError={(e) => {
              e.currentTarget.src = placeholderDataUrl(card.imgIdx);
            }}
          />
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Memory Game
   ============================================================ */
function MemoryGame({ onPairFound, onWin, revealedHints }) {
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [attempts, setAttempts] = useState(0);
  const [pairsFound, setPairsFound] = useState(0);
  const [busy, setBusy] = useState(false);

  const totalPairs = uploadedImages.length;

  const reset = useMemo(
    () => () => {
      const deck = [];
      uploadedImages.forEach((src, i) => {
        deck.push({ id: `${i}-a`, imgIdx: i, src, variant: VARIANTS[i % VARIANTS.length] });
        deck.push({ id: `${i}-b`, imgIdx: i, src, variant: VARIANTS[i % VARIANTS.length] });
      });
      setCards(shuffle(deck));
      setFlipped([]);
      setMatched([]);
      setAttempts(0);
      setPairsFound(0);
      setBusy(false);
    },
    []
  );

  useEffect(() => {
    reset();
  }, [reset]);

  const handleClick = (idx) => {
    if (busy) return;
    if (matched.includes(idx)) return;
    if (flipped.includes(idx)) return;
    if (flipped.length >= 2) return;

    const next = [...flipped, idx];
    setFlipped(next);
    if (next.length === 2) {
      setBusy(true);
      setAttempts((a) => a + 1);
      const [a, b] = next;
      if (cards[a].imgIdx === cards[b].imgIdx) {
        // Match!
        setTimeout(() => {
          setMatched((m) => [...m, a, b]);
          setFlipped([]);
          setBusy(false);
          const newPairs = pairsFound + 1;
          setPairsFound(newPairs);
          onPairFound(newPairs);
          if (newPairs >= totalPairs) onWin();
        }, 480);
      } else {
        setTimeout(() => {
          setFlipped([]);
          setBusy(false);
        }, 900);
      }
    }
  };

  return (
    <div className="memory-wrap" data-testid="memory-wrap">
      <div className="memory-stats">
        <span className="stat">
          <span className="lbl">Paare</span>
          <span className="val" data-testid="stat-pairs">{pairsFound}/{totalPairs}</span>
        </span>
        <span className="stat">
          <span className="lbl">Versuche</span>
          <span className="val" data-testid="stat-attempts">{attempts}</span>
        </span>
        <button className="btn-reset" onClick={reset} data-testid="reset-btn">
          <RefreshCw size={12} />
          Neustart
        </button>
      </div>
      <div className="memory-grid">
        {cards.map((c, idx) => (
          <Card
            key={c.id}
            card={c}
            flipped={flipped.includes(idx) || matched.includes(idx)}
            matched={matched.includes(idx)}
            onClick={() => handleClick(idx)}
          />
        ))}
      </div>

      {pairsFound >= totalPairs && (
        <div className="win-msg" data-testid="win-msg">
          <div className="win-msg-title">{WIN_MSG_TITLE}</div>
          <p>{WIN_MSG_BODY}</p>
          <p style={{ marginTop: 6 }}>
            <strong>{WIN_MSG_HINT}</strong>
          </p>
        </div>
      )}

      {revealedHints > 0 && (
        <div className="hints" data-testid="hints">
          <div className="hints-title">
            <Lightbulb size={12} />
            Deine Hinweise
          </div>
          {hints.slice(0, revealedHints).map((h, i) => (
            <div key={i} className="hint-item">
              <span className="num">{i + 1}</span>
              <span>{h.replace(/^Hinweis \d+:\s*/, "")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Doodles background
   ============================================================ */
const Doodles = () => (
  <div className="doodles" aria-hidden="true">
    <div className="doodle d-1">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
        <path d="M10 0l2.5 6.5L20 8l-6 5 1.5 7L10 16l-5.5 4L6 13 0 8l7.5-1.5L10 0z" />
      </svg>
    </div>
    <div className="doodle d-2">
      <svg width="22" height="20" viewBox="0 0 24 22" fill="currentColor">
        <path d="M12 21s-9-5.5-9-12a5 5 0 0 1 9-3 5 5 0 0 1 9 3c0 6.5-9 12-9 12z" />
      </svg>
    </div>
    <div className="doodle d-3">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
        <circle cx="12" cy="12" r="9" strokeDasharray="4 3" />
      </svg>
    </div>
    <div className="doodle d-4">
      <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
        <path d="M10 0l2.5 6.5L20 8l-6 5 1.5 7L10 16l-5.5 4L6 13 0 8l7.5-1.5L10 0z" />
      </svg>
    </div>
    <div className="doodle d-5">
      <svg width="22" height="20" viewBox="0 0 24 22" fill="currentColor">
        <path d="M12 21s-9-5.5-9-12a5 5 0 0 1 9-3 5 5 0 0 1 9 3c0 6.5-9 12-9 12z" />
      </svg>
    </div>
    <div className="doodle d-6">
      <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
        <path d="M10 0l2.5 6.5L20 8l-6 5 1.5 7L10 16l-5.5 4L6 13 0 8l7.5-1.5L10 0z" />
      </svg>
    </div>
    <div className="doodle d-7">
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
        <path d="M4 12c2-6 8-6 8 0s6 6 8 0" strokeLinecap="round" />
      </svg>
    </div>
  </div>
);

/* ============================================================
   Reveal — after countdown ends
   ============================================================ */
function Reveal() {
  const [open, setOpen] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const handleClick = () => {
    setOpen(true);
    setConfetti(true);
    setTimeout(() => setConfetti(false), 3800);
  };
  return (
    <section className="reveal-wrap" data-testid="reveal">
      <Confetti active={confetti} />
      <div className="reveal-title">{REVEAL_TITLE}</div>
      <div className="reveal-sub">{REVEAL_SUB}</div>
      {!open ? (
        <button className="reveal-btn" onClick={handleClick} data-testid="reveal-btn">
          <Gift size={18} style={{ marginRight: 8, verticalAlign: "middle" }} />
          Zeig mir das Date
        </button>
      ) : (
        <div className="reveal-content" data-testid="reveal-content">
          {REVEAL_PLACEHOLDER}
        </div>
      )}
    </section>
  );
}

/* ============================================================
   APP
   ============================================================ */
function App() {
  const [revealedHints, setRevealedHints] = useState(0);
  const [winConfetti, setWinConfetti] = useState(false);
  const { done: countdownDone } = useCountdown(COUNTDOWN_TARGET);

  const handlePairFound = (pairs) => {
    const total = uploadedImages.length;
    const revealed = Math.min(
      hints.length,
      Math.ceil((pairs * hints.length) / total)
    );
    setRevealedHints((h) => Math.max(h, revealed));
  };
  const handleWin = () => {
    setWinConfetti(true);
    setTimeout(() => setWinConfetti(false), 3600);
  };

  if (SITE_OFFLINE) {
    return (
      <div className="App">
        <div className="bg-orbs">
          <div className="orb orb-a" />
          <div className="orb orb-b" />
          <div className="orb orb-c" />
          <div className="orb orb-d" />
        </div>
        <Doodles />
        <div className="container" data-testid="offline-screen">
          <div
            style={{
              minHeight: "80vh",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <div className="hey">hey du …</div>
            <h1 className="headline" data-testid="offline-title">
              <span className="g1">{OFFLINE_TITLE}</span>
            </h1>
            <p className="subheadline" style={{ margin: "8px auto 0" }}>
              {OFFLINE_TEXT}
            </p>
            <div className="countdown-footer" style={{ marginTop: 18 }}>
              {OFFLINE_SUB}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="bg-orbs">
        <div className="orb orb-a" />
        <div className="orb orb-b" />
        <div className="orb orb-c" />
        <div className="orb orb-d" />
      </div>
      <Doodles />
      <Confetti active={winConfetti} />

      <div className="container">
        <div className="hey">hey du …</div>
        <h1 className="headline" data-testid="headline">
          <span className="g1">Etwas</span> wartet
          <br />
          <span className="g2">auf dich.</span>
        </h1>
        <p className="subheadline">
          Aber noch nicht sofort. Ein kleines bisschen Spannung muss sein.
        </p>

        {!countdownDone ? (
          <Countdown target={COUNTDOWN_TARGET} />
        ) : (
          <Reveal />
        )}

        <div className="pill-row">
          {MICRO_TEXTS.map((t, i) => (
            <span key={i} className="pill">{t}</span>
          ))}
        </div>

        <section className="section" data-testid="memory-section">
          <div className="section-head">
            <h2 className="section-title">
              <span className="accent">Zeitvertreib</span> bis dahin?
            </h2>
            <p className="section-sub">
              Gewinn das Memory und sammle kleine Hinweise.
            </p>
          </div>
          <MemoryGame
            onPairFound={handlePairFound}
            onWin={handleWin}
            revealedHints={revealedHints}
          />
        </section>

        <footer className="foot">
          <Sparkles size={12} style={{ display: "inline", marginRight: 6, color: "var(--yellow)" }} />
          made with a bit of mystery
        </footer>
      </div>
    </div>
  );
}

export default App;
